const LOCAL_PROXY_BASE = '/api'
const PROD_HOST_HINTS = ['agents.artistascend.app', 'api.artistascend.app']

const env = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {}
const configuredBaseUrl = typeof env.VITE_API_BASE_URL === 'string' ? env.VITE_API_BASE_URL.trim() : ''
const forceRemoteApi =
  typeof env.VITE_FORCE_REMOTE_API === 'string' && env.VITE_FORCE_REMOTE_API.toLowerCase() === 'true'
const isBrowser = typeof window !== 'undefined'
const localHostnames = new Set(['localhost', '127.0.0.1', '0.0.0.0'])
const runtimeHostname =
  isBrowser && typeof window.location?.hostname === 'string' ? window.location.hostname.toLowerCase() : ''
const isLocalHost =
  runtimeHostname && (localHostnames.has(runtimeHostname) || runtimeHostname.endsWith('.localhost') || runtimeHostname.endsWith('.local'))
const configuredLooksProduction =
  !!configuredBaseUrl &&
  PROD_HOST_HINTS.some((hint) => configuredBaseUrl.toLowerCase().includes(hint.toLowerCase()))
const shouldUseLocalProxy =
  Boolean(env.DEV) && !forceRemoteApi && isLocalHost && (!configuredBaseUrl || configuredLooksProduction)

const SAME_ORIGIN_FRONTEND_HOSTS = new Set(['agents.artistascend.app', 'www.agents.artistascend.app'])
const shouldUseSameOriginProxy =
  Boolean(runtimeHostname) &&
  SAME_ORIGIN_FRONTEND_HOSTS.has(runtimeHostname) &&
  !forceRemoteApi

const resolveRawBaseUrl = () => {
  if (!forceRemoteApi && shouldUseLocalProxy) {
    return LOCAL_PROXY_BASE
  }
  if (configuredBaseUrl) {
    return configuredBaseUrl
  }
  if (!forceRemoteApi && shouldUseSameOriginProxy) {
    return LOCAL_PROXY_BASE
  }
  return LOCAL_PROXY_BASE
}

const normalizeBaseUrl = (value) => {
  if (!value) return '/api'

  // Absolute URLs are routed through CDN/proxy; ensure they target the API mount.
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value)
      if (!url.pathname || url.pathname === '/') {
        url.pathname = '/api'
      }
      return url.toString().replace(/\/$/, '')
    } catch {
      return '/api'
    }
  }

  const cleaned = value.startsWith('/') ? value : `/${value}`
  const withoutTrailingSlash = cleaned.replace(/\/$/, '')
  return withoutTrailingSlash || '/api'
}

const API_BASE_URL = normalizeBaseUrl(resolveRawBaseUrl())
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value)
const buildUrl = (path) => {
  if (isAbsoluteUrl(path)) {
    return path
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalizedPath}`
}
export const ARTIST_ID_STORAGE_KEY = 'artistascend:artistId'
export const AUTH_EVENTS = {
  unauthorized: 'agents:auth:unauthorized',
}
export { API_BASE_URL }

let runtimeAuthToken = ''
let unauthorizedNotified = false
let unauthorizedResetTimer = null

export const setRuntimeAuthToken = (token) => {
  runtimeAuthToken = token || ''
}

export const clearRuntimeAuthToken = () => {
  runtimeAuthToken = ''
}

export const getAuthToken = () => runtimeAuthToken

const getWindow = () => (typeof window === 'undefined' ? undefined : window)

export const getActiveArtistId = () => {
  const w = getWindow()
  if (!w) return ''
  return w.localStorage.getItem(ARTIST_ID_STORAGE_KEY) || ''
}

export const setActiveArtistId = (artistId) => {
  const w = getWindow()
  if (!w) return
  if (artistId) {
    w.localStorage.setItem(ARTIST_ID_STORAGE_KEY, artistId)
  } else {
    w.localStorage.removeItem(ARTIST_ID_STORAGE_KEY)
  }
}

const dispatchAuthEvent = (type, detail) => {
  const w = getWindow()
  if (!w) return
  try {
    w.dispatchEvent(new CustomEvent(type, { detail }))
  } catch {
    try {
      w.dispatchEvent(new Event(type))
    } catch {
      /* no-op */
    }
  }
}

const isObject = (value) =>
  typeof value === 'object' && value !== null && !(value instanceof FormData)

const parseErrorPayload = async (response) => {
  let payload = null
  try {
    payload = await response.clone().json()
  } catch {
    payload = null
  }
  if (payload?.detail) {
    if (typeof payload.detail === 'string') {
      return { message: payload.detail, payload }
    }
    if (typeof payload.detail === 'object') {
      const title = payload.detail.title || payload.detail.type || 'Error'
      const message =
        payload.detail.message ||
        payload.detail.detail ||
        JSON.stringify(payload.detail)
      return { message: `${title}: ${message}`, payload }
    }
  }
  if (payload?.message) {
    return { message: payload.message, payload }
  }
  if (typeof payload === 'string') {
    return { message: payload, payload }
  }
  try {
    const text = await response.clone().text()
    if (text) {
      const looksHtml = /<\/?[a-z][\s\S]*>/i.test(text.trim())
      if (looksHtml) {
        return {
          message: `${response.status} ${response.statusText}`.trim() || 'Request failed',
          payload,
        }
      }
      return { message: text, payload }
    }
  } catch {
    /* no-op */
  }
  return { message: response.statusText, payload }
}

// Default request timeout in ms. Callers can override via init.timeoutMs.
const DEFAULT_TIMEOUT_MS = 60_000

export async function request(path, { responseType = 'json', headers, body, timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = {}) {
  const finalHeaders = {
    Accept: responseType === 'json' ? 'application/json' : '*/*',
    ...headers,
  }

  const token = getAuthToken()
  if (token && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${token}`
  }

  const artistId = getActiveArtistId()
  if (artistId && !finalHeaders['X-Artist-Id']) {
    finalHeaders['X-Artist-Id'] = artistId
  }

  let finalBody = body
  if (isObject(body)) {
    finalBody = JSON.stringify(body)
    finalHeaders['Content-Type'] = 'application/json'
  }

  const controller = new AbortController()
  const timerId = setTimeout(() => controller.abort(), timeoutMs)

  let response
  try {
    response = await fetch(buildUrl(path), {
      ...init,
      headers: finalHeaders,
      body: finalBody,
      signal: controller.signal,
    })
  } catch (err) {
    if (err.name === 'AbortError') {
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`)
      timeoutError.code = 'timeout'
      throw timeoutError
    }
    throw err
  } finally {
    clearTimeout(timerId)
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearRuntimeAuthToken()
      if (!unauthorizedNotified) {
        unauthorizedNotified = true
        if (unauthorizedResetTimer) {
          clearTimeout(unauthorizedResetTimer)
        }
        unauthorizedResetTimer = setTimeout(() => {
          unauthorizedNotified = false
          unauthorizedResetTimer = null
        }, 1500)
        dispatchAuthEvent(AUTH_EVENTS.unauthorized, { url: response.url })
      }
    }
    const { message, payload } = await parseErrorPayload(response)
    const error = new Error(message || `Request failed with status ${response.status}`)
    error.payload = payload
    error.status = response.status
    error.statusText = response.statusText
    if (response.status === 403 && payload?.error === 'requires_pro') {
      error.code = 'capability_required'
      error.capability = payload?.capability
    }
    throw error
  }

  if (responseType === 'json') {
    if (response.status === 204) return null
    return response.json()
  }

  if (responseType === 'blob') {
    return response.blob()
  }

  return response
}
